// @docs:
//   - docs/core/operations/WEATHER_ADMIN_DASHBOARD.md
//
// Admin › Weather › Worldwide tiles (#727): how complete each model's worldwide tiles are,
// for its newest four runs, size by size, from the records vortex-weather's tile queue
// writes to R2. Tony switches no app over until it says 100%, run after run.

import type { WeatherTileRun, WeatherTileSize, WeatherTilesResult } from '../../services/api';
import { formatLocal, runIdToMs } from './weatherFormat';

const MODELS: { id: string; name: string }[] = [
  { id: 'ecmwf0p25', name: 'ECMWF IFS' },
  { id: 'gfs025', name: 'NOAA GFS' },
  { id: 'icon025', name: 'DWD ICON' },
];

/** Sizes in the order the table shows them. */
const SIZES = [60, 30];

const local = (iso: string | null) => (iso ? formatLocal(Date.parse(iso)) : '');

/** How many of the newest finished runs, in a row, are complete at every size. */
export function completeStreak(runs: WeatherTileRun[]): { finished: number; streak: number } {
  const finished = runs.filter((r) => r.runComplete);
  let streak = 0;
  for (const r of finished) {
    if (r.sizes.length > 0 && r.sizes.every((s) => s.complete)) streak += 1;
    else break;
  }
  return { finished: finished.length, streak };
}

/** The one line under a model's heading. */
export function verdict(runs: WeatherTileRun[]): { ready: boolean; text: string } {
  if (runs.length === 0) return { ready: false, text: 'No runs recorded yet.' };
  const { finished, streak } = completeStreak(runs);
  if (finished === 0) return { ready: false, text: 'No finished run yet.' };
  if (streak === 0) return { ready: false, text: 'Not ready: the newest finished run is not complete yet.' };
  if (streak === 1) return { ready: true, text: 'The newest finished run is complete at both sizes.' };
  return { ready: true, text: `The last ${streak} finished runs are complete at both sizes.` };
}

const MAX_HOURS_LISTED = 8;

/** What one size of one run shows: the figure, its tone, and plain lines beneath it. */
export function sizeCell(run: WeatherTileRun, size: WeatherTileSize): { figure: string; tone: 'ok' | 'warn' | 'mute'; lines: string[] } {
  if (size.expected === 0) return { figure: 'Not published yet', tone: 'mute', lines: [] };
  if (size.complete) return { figure: '100%', tone: 'ok', lines: ['Complete', `Checked in R2 ${local(size.verifiedAt ?? size.completedAt)}`] };
  const lines: string[] = [];
  if (!run.runComplete) lines.push(`Run still coming in: ${run.hours} hours so far`);
  const missing = size.missingHours;
  if (missing.length > 0) {
    const listed = missing.slice(0, MAX_HOURS_LISTED).map((h) => `+${h}`).join(', ');
    const more = missing.length > MAX_HOURS_LISTED ? ` and ${missing.length - MAX_HOURS_LISTED} more` : '';
    lines.push(`Missing ${missing.length} ${missing.length === 1 ? 'hour' : 'hours'}: ${listed}${more}`);
  } else if (run.runComplete) {
    lines.push('Every object is in; waiting for the check in R2');
  }
  if (run.pendingSteps > 0) lines.push(`${run.pendingSteps} ${run.pendingSteps === 1 ? 'hour' : 'hours'} waiting in the queue`);
  // First, so a tile refused for impossible values never reads as an ordinary gap: it stays
  // out (the same data fails the same way) and keeps the size below 100% until a new run.
  if (size.faultedTiles > 0) lines.unshift(`${size.faultedTiles} tile ${size.faultedTiles === 1 ? 'hour' : 'hours'} left out for impossible values; these keep it below 100%`);
  return { figure: `${size.percent}%`, tone: 'warn', lines };
}

const TONE = {
  ok: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  warn: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  mute: 'bg-slate-500/15 text-slate-300 border-slate-500/40',
} as const;

export function WeatherTilesPanel({ data, error }: { data: WeatherTilesResult | null; error: string | null }) {
  return (
    <section aria-label="Worldwide tiles" className="rounded-xl border border-slate-700 bg-slate-800/50">
      <div className="px-5 py-4 border-b border-slate-700 space-y-1">
        <h2 className="text-[17px] font-bold text-white">Worldwide tiles</h2>
        <p className="text-[13px] text-slate-400">
          Every forecast hour, every tile and every product, for each tile size, in the newest four runs of each model. A size is complete when the run is finished and a listing of R2 found every object. The apps still read only the Pacific.
        </p>
      </div>
      {error && <p role="status" className="px-5 py-4 text-sm text-amber-200">{error}</p>}
      {!error && !data && <p className="px-5 py-4 text-sm text-slate-400">Loading…</p>}
      {data && (
        <div className="divide-y divide-slate-700/70">
          {MODELS.map((m) => {
            const entry = data.models.find((x) => x.model === m.id);
            return <ModelTiles key={m.id} name={m.name} id={m.id} runs={entry?.runs ?? []} error={entry?.error} />;
          })}
        </div>
      )}
    </section>
  );
}

/** "15 tiles at 30° and 2 at 60°" for one run's sizes. */
function countText(run: WeatherTileRun): string {
  return [...run.sizes].sort((a, b) => a.sizeDeg - b.sizeDeg).map((s, i) => (i === 0 ? `${s.tiles} tiles at ${s.sizeDeg}°` : `${s.tiles} at ${s.sizeDeg}°`)).join(' and ');
}

/**
 * The line on coverage under a model's heading, from the runs' own records: ICON is Pacific
 * only while its worldwide regrid (`[icon] world_regrid`) is off, and a run keeps the
 * coverage it was made with. Nothing for a model whose runs are all worldwide.
 */
export function coverageNote(id: string, runs: WeatherTileRun[]): string | null {
  const newest = runs[0];
  if (!newest) return null;
  const pacific = runs.filter((r) => !r.worldwide);
  if (pacific.length === 0) return null;
  if (!newest.worldwide) {
    const counts = countText(newest);
    const why = id === 'icon025' ? ' Worldwide ICON is switched off (world_regrid in the ICON config).' : '';
    return `Pacific only${counts ? `: ${counts}` : ''}.${why}`;
  }
  return `Worldwide from run ${newest.run}. Older runs made for the Pacific only count their Pacific tiles: ${countText(pacific[0])}.`;
}

function ModelTiles({ name, id, runs, error }: { name: string; id: string; runs: WeatherTileRun[]; error?: string }) {
  const v = verdict(runs);
  const note = coverageNote(id, runs);
  return (
    <section aria-label={name} className="px-5 py-4 space-y-3">
      <div className="space-y-1">
        <h3 className="text-[15px] font-bold text-slate-100">{name}</h3>
        {note && <p className="text-sm text-cyan-200">{note}</p>}
        <p className={`text-sm ${v.ready ? 'text-emerald-300' : 'text-amber-300'}`}>{error ?? v.text}</p>
      </div>
      {runs.length > 0 && (
        <div className="overflow-x-auto">
          <table aria-label={`${name} tile coverage`} className="w-full text-sm">
            <thead className="bg-slate-900/40 text-[13px] text-slate-400">
              <tr>
                <th scope="col" className="px-3 py-2 text-left font-semibold">Run</th>
                {SIZES.map((d) => <th key={d} scope="col" className="px-3 py-2 text-left font-semibold">{d}° tiles</th>)}
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const start = runIdToMs(run.run);
                return (
                  <tr key={run.run} className="border-t border-slate-700/70 align-top">
                    <td className="px-3 py-3">
                      <span className="font-mono text-[13px] text-slate-200">{run.run}</span>
                      {start !== null && <span className="block text-[13px] text-slate-400">{formatLocal(start)}</span>}
                    </td>
                    {SIZES.map((d) => {
                      const size = run.sizes.find((s) => s.sizeDeg === d);
                      if (!size) return <td key={d} className="px-3 py-3 text-[13px] text-slate-500">Not recorded</td>;
                      const cell = sizeCell(run, size);
                      return (
                        <td key={d} className="px-3 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[13px] font-semibold border whitespace-nowrap ${TONE[cell.tone]}`}>{cell.figure}</span>
                          {cell.lines.map((line) => <span key={line} className="block mt-1 text-[13px] text-slate-300">{line}</span>)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
